import { graphql } from 'gatsby';
import React from 'react';

import { MainLayout } from '../../layouts/main-layout';
import CaseStudyLayout from '../../layouts/resource-hub/case-study-layout';
import GatedDownloadableLayout from '../../layouts/resource-hub/gated-downloadable-layout';
import UngatedContentLayout from '../../layouts/resource-hub/ungated-content-layout';
import UngatedDownloadLayout from '../../layouts/resource-hub/ungated-download-layout';
import WebinarLayout from '../../layouts/resource-hub/webinar-layout';

export const query = graphql`
	query ResourcePageQuery($_id: String!) {
		allSanityResource(filter: { _id: { eq: $_id } }) {
			nodes {
				...Resource
			}
		}
		allSanityResourceHubSettings {
			nodes {
				resourceCTA {
					_key
					eyebrowText
					headerText
					subheaderText
					ctas {
						...Cta
					}
					colorTheme
					verticalPadding {
						modulePaddingBottom
						modulePaddingTop
					}
					illustration {
						svg
					}
				}
				resourceGatedForm {
					...MarketoForm
				}
				navMenu {
					...NavMenuContainer
					...NavAbTest
				}
				footerMenu {
					...FooterMenuContainer
				}
			}
		}
		siteSettings: sanitySiteSettings(
			_id: { regex: "/(drafts.|)siteSettings/" }
		) {
			title
			openGraph {
				title
				description
				image {
					...SanityImage
				}
			}
		}
	}
`;

export function Head({ data, location }) {
	const { allSanityResource } = data || {};
	const resource = allSanityResource?.nodes?.[0];

	const authorName = resource?.author?.name;
	const canonicalUrl = resource?.canonicalUrl;
	const disableArticleSchema = resource?.disableArticleSchema;
	const enableCanonicalUrl = resource?.enableCanonicalUrl;
	const pageType = resource?.pageType;
	const publishDate = resource?.publishDate;
	const seoImageUrl = resource?.openGraph?.image?.asset?.url;
	const title = resource?.title;
	const updatedAt = resource?._updatedAt;

	const pageTitle = resource?.openGraph?.title || title;

	const articleSchema = `{
        "@context": "https://schema.org",
        "@type": "Article",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "${location.origin}/for-providers/resources/${resource?.slug?.current}"
        },
        "headline": "${pageTitle}",
        "image": "${seoImageUrl}",
        "author": {
          "@type": "Person",
          "name": "${authorName || 'Alma'}",
          "url": "https://helloalma.com/for-providers/resources/"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Alma",
          "logo": {
            "@type": "ImageObject",
            "url": ""
          }
        },
        "datePublished": "${publishDate}",
        "dateModified": "${updatedAt}"
      }`;

	const breadcrumbSchema = `{
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "For Providers",
            "item": "https://helloalma.com/for-providers/"
        },
        {
            "@type": "ListItem",
            "position": 2,
            "name": "Provider Resources",
            "item": "https://helloalma.com/for-providers/resources/"
        },
        {
            "@type": "ListItem",
            "position": 3,
            "name": "${title}",
            "item": "https://helloalma.com${location.pathname}"
        }]
    }`;

	const isArticleSchemaDisabled = disableArticleSchema || false;

	return (
		<>
			<script type='application/ld+json'>{breadcrumbSchema}</script>
			{!isArticleSchemaDisabled && pageType === 'ungatedContent' && (
				<script type='application/ld+json'>{articleSchema}</script>
			)}
			{enableCanonicalUrl &&
				canonicalUrl &&
				pageType === 'ungatedContent' && (
					<link rel='canonical' href={canonicalUrl} />
				)}
		</>
	);
}

function ResourcePage({ data }) {
	const { allSanityResource, allSanityResourceHubSettings, siteSettings } =
		data || {};

	const resourceData = allSanityResource?.nodes;
	const resourceCTA = allSanityResourceHubSettings?.nodes[0]?.resourceCTA[0];
	const resourceGatedForm =
		allSanityResourceHubSettings?.nodes[0]?.resourceGatedForm;
	const resourceHubSettings = allSanityResourceHubSettings?.nodes[0];

	const footerMenu = resourceHubSettings?.footerMenu?.[0]?.footerMenu;
	const navMenu = resourceHubSettings?.navMenu?.[0];

	return (
		<MainLayout navContent={navMenu} footerContent={footerMenu}>
			{resourceData.map((resource) => {
				const { _id } = resource;

				switch (resource.pageType) {
					case 'gatedDownloadWithPreview':
						return (
							<GatedDownloadableLayout
								key={_id}
								resource={resource}
								resourceGatedForm={resourceGatedForm}
								siteSettings={siteSettings}
							/>
						);
					case 'ungatedDownload':
						return (
							<UngatedDownloadLayout
								key={_id}
								resource={resource}
								resourceCTA={resourceCTA}
								siteSettings={siteSettings}
							/>
						);
					case 'ungatedContent':
						return (
							<UngatedContentLayout
								key={_id}
								resource={resource}
								resourceCTA={resourceCTA}
								siteSettings={siteSettings}
							/>
						);
					case 'gatedRecordedWebinar':
						return (
							<WebinarLayout
								key={_id}
								resource={resource}
								resourceGatedForm={resourceGatedForm}
								siteSettings={siteSettings}
							/>
						);
					case 'upcomingEvent':
						return (
							<WebinarLayout
								key={_id}
								upcomingEvent={true}
								resource={resource}
								siteSettings={siteSettings}
							/>
						);
					case 'caseStudy':
						return (
							<CaseStudyLayout
								key={_id}
								resource={resource}
								resourceCTA={resourceCTA}
								siteSettings={siteSettings}
							/>
						);
					default:
						return null;
				}
			})}
		</MainLayout>
	);
}

export default ResourcePage;
